import React from 'react';
import { Lock } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export const ENCRYPTION_STATEMENT =
  'Stored encrypted at rest — your data stays scrambled and unreadable, even in the unlikely event of a database breach.';

/**
 * Visual reassurance that data is encrypted at rest.
 * - variant="banner"  → Pre-input banner with PERMANENT full statement (Add Investments)
 * - variant="inline"  → Compact pill near an input. Tap/click reveals full statement (mobile-friendly).
 */
const EncryptedBadge = ({ variant = 'inline', label, className = '' }) => {
  if (variant === 'banner') {
    return (
      <div
        data-testid="encrypted-banner"
        className={`flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 sm:p-4 ${className}`}
        role="status"
        aria-label="Your investment data is encrypted at rest"
      >
        <Lock className="w-4 h-4 mt-0.5 shrink-0 text-emerald-700" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-emerald-900">
            {label || 'Encrypted at rest'}
          </p>
          <p className="text-xs text-emerald-800 mt-1 leading-relaxed">
            {ENCRYPTION_STATEMENT}
          </p>
        </div>
      </div>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="encrypted-inline-badge"
          className={`inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 active:bg-emerald-200 transition-colors cursor-pointer ${className}`}
          aria-label="This field is encrypted at rest. Tap for details."
        >
          <Lock className="w-3 h-3" aria-hidden="true" />
          {label || 'Encrypted'}
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="max-w-xs sm:max-w-sm p-3">
        <p className="text-xs leading-relaxed text-slate-700">{ENCRYPTION_STATEMENT}</p>
      </PopoverContent>
    </Popover>
  );
};

export default EncryptedBadge;
